import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColorOrString, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import React, { CSSProperties, PropsWithChildren } from 'react'

export const TypographyVariants = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'sub-heading',
  'text',
  'body',
  'body-medium',
  'secondary-body',
  'caption',
  'tag',
  'label',
  'label-semibold',
  'label-medium',
  'label-extrabold',
  'small',
  'small-medium',
  'small-semibold'
] as const

export type TextType = (typeof TypographyVariants)[number]

type CustomTextStyle = {
  fontSize: React.CSSProperties['fontSize']
  fontWeight: React.CSSProperties['fontWeight']
  lineHeight: React.CSSProperties['lineHeight']
  mobileFontSize?: React.CSSProperties['fontSize']
  mobileLineHeight?: React.CSSProperties['lineHeight']
  mobileFontWeight?: React.CSSProperties['fontWeight']
}

export type TypographyProps = {
  variant: TextType
  className?: string
  color?: TailwindColorOrString
  disabled?: boolean
  as?: keyof React.JSX.IntrinsicElements
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLHeadingElement | HTMLParagraphElement>,
  HTMLHeadingElement | HTMLParagraphElement
>

export const typography: Record<TextType, CustomTextStyle> = {
  h1: {
    fontSize: '36px',
    lineHeight: '42.2px',
    fontWeight: 700,
    mobileFontSize: '18px',
    mobileLineHeight: '21.1px',
    mobileFontWeight: 600
  },
  h2: {
    fontSize: '32px',
    lineHeight: '37.5px',
    fontWeight: 700,
    mobileFontSize: '28px',
    mobileLineHeight: '32.8px',
    mobileFontWeight: 700
  },
  h3: {
    fontSize: '28px',
    lineHeight: '32.8px',
    fontWeight: 700,
    mobileFontSize: '16px',
    mobileLineHeight: '18.8px',
    mobileFontWeight: 600
  },
  h4: {
    fontSize: '24px',
    lineHeight: '28.1px',
    fontWeight: 700,
    mobileFontSize: '18.5px',
    mobileLineHeight: '100%',
    mobileFontWeight: 600
  },
  h5: {
    fontSize: '22px',
    lineHeight: '25.8px',
    fontWeight: 700,
    mobileFontSize: '18px',
    mobileLineHeight: '100%',
    mobileFontWeight: 600
  },
  h6: {
    fontSize: '20px',
    lineHeight: '23.4px',
    fontWeight: 700
  },
  'sub-heading': {
    fontSize: '18px',
    lineHeight: '21.1px',
    fontWeight: 600
  },
  text: {
    fontSize: '16px',
    lineHeight: '18.8px',
    fontWeight: 600
  },
  body: {
    fontSize: '16px',
    lineHeight: '18.8px',
    fontWeight: 400
  },
  'body-medium': {
    fontSize: '16px',
    lineHeight: '18.8px',
    fontWeight: 600
  },
  caption: {
    fontSize: '14px',
    lineHeight: '16.4px',
    fontWeight: 700
  },
  tag: {
    fontSize: '14px',
    lineHeight: '16.4px',
    fontWeight: 600,
    mobileFontSize: '12px',
    mobileLineHeight: '14.1px',
    mobileFontWeight: 600
  },
  'secondary-body': {
    fontSize: '14px',
    lineHeight: '16.4px',
    fontWeight: 400
  },
  'label-semibold': {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 600,
    mobileFontSize: '10px',
    mobileLineHeight: '12px',
    mobileFontWeight: 600
  },
  'label-medium': {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 500
  },
  'label-extrabold': {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 800
  },
  label: {
    fontSize: '12px',
    lineHeight: 1.4,
    fontWeight: 400
  },
  small: {
    fontSize: '10px',
    lineHeight: '11.7px',
    fontWeight: 400
  },
  'small-medium': {
    fontSize: '10px',
    lineHeight: '11.7px',
    fontWeight: 500
  },
  'small-semibold': {
    fontSize: '10px',
    lineHeight: '11.7px',
    fontWeight: 600
  }
}

const Typography = ({
  className,
  variant,
  color,
  children,
  disabled,
  as,
  style: typographyStyle,
  ...props
}: PropsWithChildren<TypographyProps>) => {
  const device = useBreakPoint()
  const isMobile = device === 'mobile'
  const tag = as ?? (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant) ? variant : 'p')

  const baseStyle = typography[variant]

  const style: CSSProperties = {
    fontSize: isMobile ? (baseStyle.mobileFontSize ?? baseStyle.fontSize) : baseStyle.fontSize,
    lineHeight: isMobile
      ? (baseStyle.mobileLineHeight ?? baseStyle.lineHeight)
      : baseStyle.lineHeight,
    fontWeight: isMobile
      ? (baseStyle.mobileFontWeight ?? baseStyle.fontWeight)
      : baseStyle.fontWeight,
    color: disabled ? tailwindColors.grey.light.active : color,
    ...typographyStyle
  }

  return React.createElement(tag, { className, style, ...props }, children)
}

Typography.Link = ({
  className,
  color,
  children,
  style,
  ...props
}: PropsWithChildren<TypographyProps>) => {
  return (
    <Typography
      as="span"
      className={cn('cursor-pointer', className)}
      color={color ?? tailwindColors.blue.DEFAULT}
      style={{ textDecoration: 'underline', ...style }}
      {...props}
    >
      {children}
    </Typography>
  )
}

export default Typography
